import { Box, Tabs, Tab } from '@mui/material';

import { useThemeMode } from '../../providers/theme-provider';
import { tokens } from '../../styles/tokens';
import {
  DILETTA_TITLE,
  DILETTA_DIVIDER,
  DILETTA_BORDER,
  DILETTA_BANNER_BG,
} from '../v2/widgets/exotics-shared';

export interface CardTabItem {
  label: string;
  disabled?: boolean;
}

interface CardTabsProps {
  value: number;
  onChange: (value: number) => void;
  tabs: CardTabItem[];
}

// Shared rounded-chip tab bar used by the portfolio-performance account card and
// the performance-comparison panel, so the two can never drift apart. The sx is
// the exact treatment lifted from the account card: a top-divider wrapper with a
// row of rounded-top tabs sitting on a bottom divider line, the active tab reading
// as a chip connected to the content below.
export const CardTabs = ({ value, onChange, tabs }: CardTabsProps) => {
  const { resolvedMode } = useThemeMode();
  const isDark = resolvedMode === 'dark';

  return (
    <Box sx={{ borderTop: `1px solid ${DILETTA_DIVIDER}` }}>
      <Tabs
        value={value}
        onChange={(_, v) => onChange(v)}
        sx={{
          minHeight: 40,
          borderBottom: `1px solid ${DILETTA_DIVIDER}`,
          // Rounded-tab look instead of the sliding underline: each tab
          // is a rounded-top chip that sits on the divider line, so the
          // navigation reads as real tabs, not just text.
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-flexContainer': { gap: 0.5 },
          '& .MuiTab-root': {
            color: tokens.text.primary,
            fontSize: 14,
            fontWeight: 500,
            textTransform: 'none',
            minHeight: 40,
            px: 2,
            // -1px so the tab overlaps the container's bottom border,
            // letting the active tab's transparent bottom edge merge
            // into the content below (classic tab connection).
            mb: '-1px',
            borderRadius: '10px 10px 0 0',
            // Subtle divider-coloured outline on every tab at rest so
            // each reads as a distinct rounded-tab shape; the active tab
            // upgrades this to the brighter cyan DILETTA_BORDER below.
            border: `1px solid ${DILETTA_DIVIDER}`,
            borderBottom: 'none',
            transition: 'background-color 150ms ease, color 150ms ease',
            '&:hover': {
              backgroundColor: DILETTA_BANNER_BG,
            },
          },
          // Active tab: subtle raised background + cyan border/text,
          // bottom edge left open so it connects to the content.
          '& .Mui-selected': {
            color: DILETTA_TITLE,
            backgroundColor: DILETTA_BANNER_BG,
            borderColor: DILETTA_BORDER,
            borderBottomColor: DILETTA_BANNER_BG,
          },
          // Disabled tabs (e.g. Market Risk/Positions/Transactions on a
          // plain spot reference): faded further than the normal
          // inactive-tab colour, right down to near-black — this is
          // "disabled", not just "not selected".
          '& .Mui-disabled': {
            color: isDark ? '#0a1218' : '#000000',
            opacity: 1,
            '&:hover': { backgroundColor: 'transparent' },
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.label} label={tab.label} disabled={tab.disabled} />
        ))}
      </Tabs>
    </Box>
  );
};
